import type { UseCategoryExpressProps, UseCategoryExpressResult } from "@/types/types"
import { useEffect, useMemo, useRef } from "react"

export const useCategoryExpress = ({ items = [], componentId }: UseCategoryExpressProps): UseCategoryExpressResult => {
  const validItems = useMemo(() => {
    return items.filter(item => {
      if (item.isViewAll) {
        return item.name && item.name.trim()
      }
      return item.image && item.image.trim() && item.name && item.name.trim()
    })
  }, [items])

  const hasValidItems = validItems.length > 0

  const id = useMemo(() => componentId || `category-express-${Math.random().toString(36).substr(2, 9)}`, [componentId])

  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  const activeItemRef = useRef<number | null>(null)

  useEffect(() => {
    const isMobile = window.innerWidth < 640
    if (!isMobile) return

    const handleScroll = () => {
      let closestIndex: number | null = null
      let closestDistance: number = Infinity
      const viewportTop = window.scrollY + 100

      itemRefs.current.forEach((ref, index) => {
        if (ref && index % 2 === 0) {
          const rect = ref.getBoundingClientRect()
          const elementTop = rect.top + window.scrollY
          const elementCenter = elementTop + rect.height / 2

          if (rect.top < window.innerHeight && rect.bottom > 0) {
            const distance = Math.abs(elementCenter - viewportTop)

            if (distance < closestDistance) {
              closestIndex = index
              closestDistance = distance
            }
          }
        }
      })

      if (activeItemRef.current !== null) {
        const oldRef = itemRefs.current[activeItemRef.current]
        if (oldRef) {
          const oldItemElement = oldRef.querySelector(".category-express-item")
          if (oldItemElement) {
            oldItemElement.classList.remove("mobile-scroll-active")
          }
        }
      }

      if (closestIndex !== null && closestIndex !== activeItemRef.current) {
        const newRef = itemRefs.current[closestIndex]
        if (newRef) {
          const newItemElement = newRef.querySelector(".category-express-item")
          if (newItemElement) {
            newItemElement.classList.add("mobile-scroll-active")
            activeItemRef.current = closestIndex
          }
        }
      } else if (closestIndex === null) {
        activeItemRef.current = null
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    handleScroll()

    return () => {
      window.removeEventListener("scroll", handleScroll)
      itemRefs.current.forEach(ref => {
        if (ref) {
          const itemElement = ref.querySelector(".category-express-item")
          if (itemElement) {
            itemElement.classList.remove("mobile-scroll-active")
          }
        }
      })
      activeItemRef.current = null
    }
  }, [validItems])

  return {
    validItems,
    hasValidItems,
    id,
    itemRefs,
    activeItemRef,
  }
}
